import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
    useGetPodcast, 
    useGetPodcastEpisodesInfinite,
    useRevealPodcastEmail
} from '@/react-query/podcast-queries';
import { toast } from 'react-hot-toast';
import { EmailRevealSection } from '@/components/podcasts/EmailRevealSection';
import { 
    ArrowLeft, 
    Globe, 
    Rss, 
    Play, 
    Pause, 
    Star, 
    Users,
    Calendar,
    AlertCircle,
    Youtube,
    MapPin,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { sanitizeHtml } from '@/utils/html-utils';
import { formatListenerCount } from '@/utils/format-utils';
import { buildAppleUrl, REGION_FLAGS } from '@/utils/region-utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { mapPodcastFromApi, mapEpisodeFromApi } from '@/utils/mapper';

dayjs.extend(relativeTime);

// import { Episode } from '@/types/podcast';
import MainLayout from '@/layouts/MainLayout';
import { usePlayer } from '@/providers/player-provider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ImageWithFallback } from '@/components/common/ImageWithFallback';
import { Episode } from '@/types/podcast';
import { MatchedEpisodeCard } from '@/components/podcasts/MatchedEpisodeCard';
import { ListPlus } from 'lucide-react';
import PodcastAddToCampaignModal from '@/components/podcasts/PodcastAddToCampaignModal';

export default function PodcastDetailsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Get state from navigation (if any)
    const matchedEpisodesState = location.state?.matchedEpisodes as Episode[] | undefined;
    const episodeKeywordsState = location.state?.episodeKeywords as string[] | undefined;
    
    // Also check URL params as backup (for keywords)
    const searchParams = new URLSearchParams(location.search);
    const urlKeywords = searchParams.get('episodeKeywords')?.split(',').filter(Boolean);
    
    const highlightKeywords = episodeKeywordsState || urlKeywords || [];

    const { playEpisode, currentEpisode, isPlaying } = usePlayer();
    const { data: podcastData, isPending: isPodcastLoading } = useGetPodcast(id || '');
    const { 
        data: episodesScrollData, 
        fetchNextPage, 
        hasNextPage, 
        isFetchingNextPage, 
        status 
    } = useGetPodcastEpisodesInfinite(id || '', 20);
    
    // Flatten pages
    const episodes = episodesScrollData?.pages.flatMap(page => (page.data || []).map(mapEpisodeFromApi)) || [];
    const isLoadingEpisodes = status === 'pending';

    const revealEmailMutation = useRevealPodcastEmail();
    const [expandedEpisode, setExpandedEpisode] = useState<number | null>(null);
    const [revealedEmail, setRevealedEmail] = useState<string | null>(null);
    const [showAddToCampaignModal, setShowAddToCampaignModal] = useState(false);
    const [showAllRegions, setShowAllRegions] = useState(false);

    const handleRevealEmail = () => {
        if (revealedEmail) return;
        
        revealEmailMutation.mutate(id || '', {
            onSuccess: (data) => {
                if (data.success && data.data?.email) {
                    setRevealedEmail(data.data.email);
                }
            },
            onError: (error: any) => {
                const message = error.response?.data?.message || 'Failed to reveal email';
                toast.error(message);
            }
        });
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return '';
        }
    };

    const podcast = podcastData?.data ? mapPodcastFromApi(podcastData.data) : undefined;

    // Loading State
    if (isPodcastLoading) {
        return (
            <MainLayout>
                <div className="flex justify-center items-center h-[50vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
            </MainLayout>
        );
    }

    if (!podcast) {
         return (
            <MainLayout>
                <div className="flex flex-col items-center justify-center h-[50vh] space-y-4">
                    <h2 className="text-xl font-semibold">Podcast not found</h2>
                    <Button onClick={() => navigate('/dashboard/search')}>Back to Search</Button>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
             <div className="flex h-[calc(100vh-80px)] overflow-hidden">
                <main className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 scroll-smooth">
                    {/* Back Button */}
                    <div className="mb-4">
                        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2 pl-0 hover:bg-transparent hover:text-primary">
                             <ArrowLeft className="h-4 w-4" /> Back to Search
                        </Button>
                    </div>

                    {/* Podcast Header Card */}
                    <div className="bg-white rounded-2xl border border-border shadow-sm p-6 md:p-8">
                        <div className="flex flex-col md:flex-row gap-8">
                            {/* Image */}
                            <div className="shrink-0">
                                <ImageWithFallback 
                                    src={podcast.imageUrl} 
                                    alt={podcast.title}
                                    className="w-48 h-48 md:w-64 md:h-64 rounded-xl object-cover shadow-md border"
                                    fallbackText={podcast.title}
                                />
                            </div>

                            {/* Details */}
                            <div className="flex-1 space-y-4">
                                <div>
                                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                                        {podcast.title}
                                    </h1>
                                    <p className="text-lg text-gray-600 mt-1 font-medium">
                                        by {podcast.author}
                                    </p>
                                </div>

                                <div className="flex flex-wrap gap-3 text-sm text-gray-500 items-center">
                                    {podcast.language && (
                                        <Badge variant="secondary" className="uppercase">{podcast.language}</Badge>
                                    )}
                                    {podcast.isExplicit && (
                                        <Badge variant="destructive" className="uppercase text-[10px] h-5 gap-1">
                                            <AlertCircle className="h-3 w-3" /> Explicit
                                        </Badge>
                                    )}
                                    {podcast.episodeCount && (
                                        <span className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-md text-slate-700 font-medium">
                                            <Rss className="h-3.5 w-3.5 text-slate-500" /> {podcast.episodeCount} Episodes
                                        </span>
                                    )}
                                    {podcast.appleRating && (
                                         (podcast.externalLinks?.applePodcasts || podcast.itunesId) ? (
                                        <a 
                                            href={podcast.externalLinks?.applePodcasts || (podcast.itunesId ? buildAppleUrl(podcast.itunesId, podcast.primaryMarket) : '#')}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-md text-amber-700 font-medium border border-amber-200 hover:bg-amber-100 transition-colors"
                                        >
                                            <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> 
                                            {podcast.appleRating} ({podcast.appleRatingCount || 0})
                                        </a>
                                        ) : (
                                        <span className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-md text-amber-700 font-medium border border-amber-200">
                                            <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> 
                                            {podcast.appleRating} ({podcast.appleRatingCount || 0})
                                        </span>
                                        )
                                    )}
                                    {podcast.estimatedListeners != null && podcast.estimatedListeners > 0 && (
                                         <span className="flex items-center gap-1 bg-indigo-50 px-2 py-1 rounded-md text-indigo-700 font-medium border border-indigo-200">
                                            <Users className="h-3.5 w-3.5 text-indigo-600" /> 
                                            {formatListenerCount(podcast.estimatedListeners)} Listeners
                                        </span>
                                    )}
                                    {podcast.youtubeSubscriberCount && (
                                        <a 
                                            href={podcast.youtubeUrl || (podcast.youtubeHandle ? `https://youtube.com/${podcast.youtubeHandle}` : '#')}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={cn(
                                                "flex items-center gap-1 bg-red-50 px-2 py-1 rounded-md text-red-700 font-medium border border-red-200 transition-colors",
                                                (podcast.youtubeUrl || podcast.youtubeHandle) && "hover:bg-red-100 cursor-pointer"
                                            )}
                                        >
                                            <Youtube className="h-3.5 w-3.5 text-red-600" /> 
                                            {podcast.youtubeSubscriberCount} Subscribers
                                        </a>
                                    )}
                                    {podcast.lastEpisodeDate && (
                                         <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md text-slate-600 font-medium border border-slate-200">
                                            <Calendar className="h-3.5 w-3.5 text-slate-500" /> 
                                            Last Ep: {dayjs(podcast.lastEpisodeDate).fromNow()}
                                        </span>
                                    )}
                                </div>
                                
                                <div className="flex flex-wrap gap-4 pt-4 items-center">
                                     {podcast.website && (
                                        <a href={podcast.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors text-sm font-medium">
                                            <Globe className="h-4 w-4" /> Website
                                        </a>
                                     )}

                                    {(podcast.website && ((podcast.externalLinks?.applePodcasts || podcast.itunesId) || (podcast.youtubeUrl || podcast.youtubeHandle))) && (
                                        <div className="w-px h-4 bg-gray-300 mx-1" />
                                    )}

                                     {/* Apple Podcasts */}
                                     {(podcast.externalLinks?.applePodcasts || podcast.itunesId) && (
                                         <a
                                            href={podcast.externalLinks?.applePodcasts || (podcast.itunesId ? buildAppleUrl(podcast.itunesId, podcast.primaryMarket) : '#')}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="hover:opacity-80 transition-opacity hover:scale-105 transform duration-200"
                                            title="Listen on Apple Podcasts"
                                         >
                                            <svg className="h-7 w-7" viewBox="0 0 24 24" fill="#9933CC" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5.34 0A5.328 5.328 0 000 5.34v13.32A5.328 5.328 0 005.34 24h13.32A5.328 5.328 0 0024 18.66V5.34A5.328 5.328 0 0018.66 0zm6.525 2.568c2.336 0 4.448.902 6.056 2.587 1.224 1.272 1.912 2.619 2.264 4.392.12.59.12 2.2.007 2.864a8.506 8.506 0 01-3.24 5.296c-.608.46-2.096 1.261-2.336 1.261-.088 0-.096-.091-.056-.46.072-.592.144-.715.48-.856.536-.224 1.448-.874 2.008-1.435a7.644 7.644 0 002.008-3.536c.208-.824.184-2.656-.048-3.504-.728-2.696-2.928-4.792-5.624-5.352-.784-.16-2.208-.16-3 0-2.728.56-4.984 2.76-5.672 5.528-.184.752-.184 2.584 0 3.336.456 1.832 1.64 3.512 3.192 4.512.304.2.672.408.824.472.336.144.408.264.472.856.04.36.03.464-.056.464-.056 0-.464-.176-.896-.384l-.04-.03c-2.472-1.216-4.056-3.274-4.632-6.012-.144-.706-.168-2.392-.03-3.04.36-1.74 1.048-3.1 2.192-4.304 1.648-1.737 3.768-2.656 6.128-2.656zm.134 2.81c.409.004.803.04 1.106.106 2.784.62 4.76 3.408 4.376 6.174-.152 1.114-.536 2.03-1.216 2.88-.336.43-1.152 1.15-1.296 1.15-.023 0-.048-.272-.048-.603v-.605l.416-.496c1.568-1.878 1.456-4.502-.256-6.224-.664-.67-1.432-1.064-2.424-1.246-.64-.118-.776-.118-1.448-.008-1.02.167-1.81.562-2.512 1.256-1.72 1.704-1.832 4.342-.264 6.222l.413.496v.608c0 .336-.027.608-.06.608-.03 0-.264-.16-.512-.36l-.034-.011c-.832-.664-1.568-1.842-1.872-2.997-.184-.698-.184-2.024.008-2.72.504-1.878 1.888-3.335 3.808-4.019.41-.145 1.133-.22 1.814-.211zm-.13 2.99c.31 0 .62.06.844.178.488.253.888.745 1.04 1.259.464 1.578-1.208 2.96-2.72 2.254h-.015c-.712-.331-1.096-.956-1.104-1.77 0-.733.408-1.371 1.112-1.745.224-.117.534-.176.844-.176zm-.011 4.728c.988-.004 1.706.349 1.97.97.198.464.124 1.932-.218 4.302-.232 1.656-.36 2.074-.68 2.356-.44.39-1.064.498-1.656.288h-.003c-.716-.257-.87-.605-1.164-2.644-.341-2.37-.416-3.838-.218-4.302.262-.616.974-.966 1.97-.97z" />
                                            </svg>
                                         </a>
                                     )}

                                     {/* YouTube */}
                                     {(podcast.youtubeUrl || podcast.youtubeHandle) && (
                                         <a
                                            href={podcast.youtubeUrl || `https://youtube.com/${podcast.youtubeHandle}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#FF0000] hover:opacity-80 transition-opacity hover:scale-105 transform duration-200"
                                            title="Watch on YouTube"
                                        >
                                            <svg className="h-9 w-9 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                                            </svg>
                                        </a>
                                     )}
                                </div>

                                <div className="pt-4 border-t border-gray-100">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <EmailRevealSection 
                                            hasEmail={podcast.hasEmail}
                                            revealedEmail={revealedEmail || podcast.email || null}
                                            isLoading={revealEmailMutation.isPending}
                                            onReveal={handleRevealEmail}
                                        />
                                        
                                        <div className="flex flex-col md:flex-row gap-2">
                                            <Button 
                                                variant="outline"
                                                onClick={() => setShowAddToCampaignModal(true)}
                                                className="gap-2"
                                            >
                                                <ListPlus className="h-4 w-4" /> Add to Campaign
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <PodcastAddToCampaignModal
                            open={showAddToCampaignModal}
                            onClose={() => setShowAddToCampaignModal(false)}
                            podcast={podcast}
                        />

                         <div className="mt-8 prose prose-slate max-w-none text-gray-600 text-sm">
                            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(podcast.description || '') }} />
                        </div>
                    </div>

                    {/* Regional Ratings Section */}
                    {podcast.regionalRatings && podcast.regionalRatings.length > 0 && (() => {
                        const validRatings = podcast.regionalRatings.filter(r => (r.rating && r.rating > 0) || (r.reviewCount && r.reviewCount > 0));
                        if (validRatings.length === 0) return null;
                        const primary = podcast.primaryMarket 
                            ? validRatings.find(r => r.regionCode === podcast.primaryMarket) 
                            : null;
                        const others = validRatings.filter(r => r.regionCode !== podcast.primaryMarket);
                        const COLLAPSED_LIMIT = 3;
                        const hasMore = others.length > COLLAPSED_LIMIT;

                        return (
                        <div className="bg-white rounded-2xl border border-border shadow-sm p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-amber-600" />
                                    <h2 className="text-sm font-semibold text-gray-900">Ratings by Region</h2>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {/* Primary market — inline pill, amber highlight */}
                                {primary && (
                                    <a
                                        key={primary.regionCode}
                                        href={podcast.itunesId ? buildAppleUrl(podcast.itunesId, primary.regionCode) : '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors text-xs"
                                    >
                                        <span>{REGION_FLAGS[primary.regionCode]}</span>
                                        <span className="font-medium text-amber-800 uppercase">{primary.regionCode}</span>
                                        <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                                        <span className="font-semibold text-gray-900">{primary.rating?.toFixed(1) || '—'}</span>
                                        <span className="text-amber-600/70">({primary.reviewCount?.toLocaleString() || 0})</span>
                                        <span className="text-[9px] font-medium text-amber-700 bg-amber-100 px-1 rounded">PRIMARY</span>
                                    </a>
                                )}

                                {/* Other regions */}
                                {(showAllRegions ? others : others.slice(0, COLLAPSED_LIMIT)).map((rr) => (
                                        <a
                                            key={rr.regionCode}
                                            href={podcast.itunesId ? buildAppleUrl(podcast.itunesId, rr.regionCode) : '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors text-xs"
                                        >
                                            <span>{REGION_FLAGS[rr.regionCode]}</span>
                                            <span className="font-medium text-gray-700 uppercase">{rr.regionCode}</span>
                                            <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                                            <span className="font-semibold text-gray-900">{rr.rating?.toFixed(1) || '—'}</span>
                                            <span className="text-gray-400">({rr.reviewCount?.toLocaleString() || 0})</span>
                                        </a>
                                    ))}
                                    {hasMore && !showAllRegions && (
                                        <button
                                            onClick={() => setShowAllRegions(true)}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors text-xs text-primary font-medium"
                                        >
                                            +{others.length - COLLAPSED_LIMIT} more
                                            <ChevronDown className="h-3 w-3" />
                                        </button>
                                    )}
                                    {hasMore && showAllRegions && (
                                        <button
                                            onClick={() => setShowAllRegions(false)}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors text-xs text-primary font-medium"
                                        >
                                            Show less
                                            <ChevronUp className="h-3 w-3" />
                                        </button>
                                    )}
                                </div>
                        </div>
                        );
                    })()}

                    {/* Matched Episodes Section (if any) */}
                    {matchedEpisodesState && matchedEpisodesState.length > 0 && highlightKeywords.length > 0 && (
                        <div className="mb-8 space-y-4">
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-bold text-gray-900 bg-yellow-50 px-2 py-0.5 rounded-md border border-yellow-200 inline-flex items-center gap-2">
                                    <Star className="size-4 text-amber-500 fill-amber-500" />
                                    Top Matching Episodes
                                </h2>
                                <span className="text-sm text-slate-500">
                                    (Matched "{highlightKeywords.join(', ')}")
                                </span>
                            </div>

                            <div className="grid gap-3">
                                {matchedEpisodesState.map((episode) => (
                                    <MatchedEpisodeCard
                                        key={`match-${episode.id}`}
                                        episode={episode}
                                        highlightKeywords={highlightKeywords}
                                        isCurrent={currentEpisode?.id === episode.id}
                                        isPlaying={isPlaying}
                                        onPlay={() => playEpisode(episode)}
                                        formatDate={formatDate}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Episodes List */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold text-gray-900">Episodes</h2>
                        
                        {isLoadingEpisodes ? (
                             <div className="py-8 text-center text-gray-500">Loading episodes...</div>
                        ) : episodes.length > 0 ? (
                            <div className="grid gap-4">
                                {episodes.map((episode) => {
                                    const isCurrent = currentEpisode?.id === episode.id;
                                    const isExpanded = expandedEpisode === episode.id;
                                    
                                    return (
                                        <div key={episode.id} className="bg-white rounded-xl border border-border p-4 transition-all hover:bg-slate-50">
                                            <div className="flex items-start gap-4">
                                                <Button
                                                    size="icon"
                                                    className={cn(
                                                        "rounded-full h-10 w-10 shrink-0 shadow-sm",
                                                        isCurrent && isPlaying ? "bg-primary hover:bg-primary/90" : "bg-white border hover:bg-slate-100 text-slate-700"
                                                    )}
                                                    onClick={() => playEpisode(episode)}
                                                >
                                                    {isCurrent && isPlaying ? (
                                                        <Pause className="h-4 w-4 text-white" />
                                                    ) : (
                                                        <Play className="h-4 w-4 ml-0.5" />
                                                    )}
                                                </Button>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-start gap-2">
                                                        <h3 className="font-semibold text-gray-900 line-clamp-2 md:line-clamp-1">
                                                            {episode.title}
                                                        </h3>
                                                        <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                                                            {formatDate(episode.publishedAt)}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="flex flex-col gap-1 mt-1">
                                                        <div 
                                                            className={cn(
                                                                "text-sm text-gray-600 cursor-pointer transition-colors hover:text-gray-900",
                                                                !isExpanded && "line-clamp-2"
                                                            )}
                                                            onClick={() => setExpandedEpisode(isExpanded ? null : episode.id)}
                                                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(episode.description || '') }} 
                                                        />
                                                        {isExpanded && (
                                                            <button 
                                                                className="text-xs text-primary font-medium hover:underline self-start mt-1"
                                                                onClick={() => setExpandedEpisode(null)}
                                                            >
                                                                Show Less
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-10 border rounded-xl border-dashed">
                                No episodes found for this podcast.
                            </div>
                        )}

                        {hasNextPage && (
                            <Button 
                                variant="outline" 
                                onClick={() => fetchNextPage()} 
                                disabled={isFetchingNextPage}
                                className="w-full"
                            >
                                {isFetchingNextPage ? 'Loading more...' : 'Load More Episodes'}
                            </Button>
                        )}
                    </div>
                </main>
             </div>
        </MainLayout>
    );
}
